import { AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';

import { Box, Center, Hide, Show, useMediaQuery } from '@vimeo/bokeh';
import { theme } from '@vimeo/bokeh-theme';
import { ClipLayout } from '@vimeo/clip-ui/components/ClipLayout';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import {
  deriveReferrerPage,
  deriveViewerAuthStatus,
  usePicoEffect,
  useShowcaseTracking,
} from '@vimeo/picox';
import { useViewer } from '@vimeo/viewer';
import { useIsClipView } from '@vimeo/viewer-showcase-ui/hooks/use-is-clip-view';
import { isArchivedOrNonLive } from '@vimeo/viewer-showcase-ui/utils/helper';
import { commonWrapperLeft, commonWrapperWidth } from '@vimeo/viewer-showcase-ui/utils/style';

import { useCanInteract } from '../hooks/use-can-interact';
import { useCommentsPermissions } from '../hooks/use-comments-permissions';
import { useGetInfiniteVideos } from '../hooks/use-get-playlist-videos';
import { useGetShowcase } from '../hooks/use-get-showcase';
import { useGetShowcaseVideo } from '../hooks/use-get-showcase-video';
import { useHeightMessenger } from '../hooks/use-height-messenger';
import { useInteractionTools } from '../hooks/use-interaction-tools';
import { LoginModalProvider } from '../hooks/use-login-modal';
import { useSelectedId } from '../hooks/use-selected-id';
import { useShowNewCustomizeControls } from '../hooks/use-show-new-customize-controls';
import { useStatus } from '../hooks/use-status';
import { useSyncLayoutState } from '../hooks/use-sync-layout-state';
import {
  BannerModule,
  EventActionBarModule,
  GalleryPlaylistModule,
  IframePlayerModule,
  LiveEngagementToolModule,
  ShowcaseNavigationModule,
  VideoActionBarModule,
  VimeoAiModule,
} from '../modules';
import { ClipPlaylistModule } from '../modules/ClipPlaylistModule';
import { CommentsModule } from '../modules/CommentsModule';
import { VideoDetailsModule } from '../modules/VideoDetailsModule';
import { useAlbumStore } from '../state/albumStore';
import { bpShowcaseLoadVideo, bpShowcasePageLoad } from '../utils/analytics';

export const ShowcasePage = () => {
  const { isOffsite, isShowcaseEmbedded, albumId, albumOwnerId, albumPrivacy, noOfVideos } =
    useAlbumStore((state) => ({
      isOffsite: state.isOffsite,
      isShowcaseEmbedded: state.isShowcaseEmbedded,
      albumId: state.albumId,
      albumOwnerId: state.albumOwnerId,
      albumPrivacy: state.albumPrivacy,
      noOfVideos: state.noOfVideos,
    }));
  const bannerHeight = useAlbumStore((state) => state.bannerHeight);
  const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const { selectedId, isVideoClip } = useSelectedId();
  const viewer = useViewer();
  const {
    playback: {
      enableAskAi,
      enableComments,
      enableLike,
      enableShare,
      showDateAdded,
      showDescription,
      showProfileImage,
      showProfileName,
      showTitle,
      showViews,
    },
    user,
    layout,
    hideNav,
    showVideoDetails,
    isLoading: isShowcaseLoading,
  } = useGetShowcase();
  const { clips, isShowcaseEmpty } = useGetInfiniteVideos();
  const isClipView = useIsClipView();

  const { clip, isLoading: isClipLoading } = useGetShowcaseVideo(selectedId);
  const clipId = clip?.clipId ?? '';
  const clipRequestId = clip?.clipRequestId ?? '';
  // The Clip Chat Room ID
  const roomId = clip?.live?.chat?.roomId ?? 0;
  const { canInteract } = useCanInteract(clip?.metadata?.interactions?.interact);
  const { showComments } = useCommentsPermissions(clip?.page?.comments || false, canInteract);

  const { isVideoProcessing } = useStatus({
    clip,
    isLoading: isClipLoading,
  });

  const isNewCustomizeControls = useShowNewCustomizeControls();
  const showShareView = Boolean(clip?.page?.share) && enableShare;

  const showLikeView = Boolean(clip?.page?.like) && enableLike;
  const showDownload = Boolean(clipId && clip?.privacy.download && isArchivedOrNonLive(clip));
  const canLike = Boolean(clip?.metadata?.interactions?.like);
  const canSeeVimeoAi =
    enableAskAi &&
    !!clip?.metadata?.interactions?.askAiViewer &&
    !clip?.metadata?.interactions?.askAiViewer?.disabled;
  const showCommentsView = showComments && enableComments && !isShowcaseEmbedded && !isOffsite;

  const {
    isSideBySideActive,
    isSideModuleOpen,
    activeSideModule,
    activeMobileDrawer,
    setActiveMobileDrawer,
    setActiveSideModule,
    toggleSideBySideView,
  } = useLayout();
  const { trackShowcasePageDisplayed } = useShowcaseTracking();
  const mediaAreaRef = useRef<HTMLDivElement>(null);
  const pageLoadEventSent = useRef(false);

  useSyncLayoutState();
  useHeightMessenger();

  const { isInteractionToosEnabled, isLiveEvent, setIsInteractionToolsEnabled } =
    useInteractionTools();

  // Send BP event whenever a showcase page loads (original behaviour — no extra gates)
  useEffect(() => {
    if (pageLoadEventSent.current) return;
    bpShowcasePageLoad(user?.uri || '', viewer);
    pageLoadEventSent.current = true;
  }, [user, viewer]);

  // Send PicoX event whenever a showcase page loads
  usePicoEffect(
    () => {
      if (isShowcaseLoading || !albumOwnerId || !albumPrivacy) return false;
      const showcasePageLayout = layout === 'featured' ? 'player' : 'grid';
      trackShowcasePageDisplayed({
        showcaseId: albumId.toString(),
        showcaseOwnerId: albumOwnerId.toString(),
        showcaseVideoCount: noOfVideos ?? 0,
        showcasePageLayout,
        showcasePagePrivacy: albumPrivacy,
        showcasePageViewerAuthStatus: deriveViewerAuthStatus(viewer),
        referrerPage: deriveReferrerPage(),
      });
    },
    [albumId, albumOwnerId, albumPrivacy, noOfVideos, layout, isShowcaseLoading],
    { once: true },
  );

  // Close AI side module when navigating to a video that doesn't support Ask AI
  useEffect(() => {
    if (!canSeeVimeoAi) {
      if (activeSideModule === 'VIMEO_AI') setActiveSideModule('');
      if (activeMobileDrawer === 'VIMEO_AI') setActiveMobileDrawer('');
    }
  }, [
    canSeeVimeoAi,
    activeSideModule,
    activeMobileDrawer,
    setActiveSideModule,
    setActiveMobileDrawer,
  ]);

  // Turn off side by side view when in gallery view
  useEffect(() => {
    if (!isClipView && isSideBySideActive) {
      toggleSideBySideView();
    }
  }, [isClipView, isSideBySideActive, toggleSideBySideView]);

  // Send BP event whenever user selects a video
  useEffect(() => {
    const clip = clips?.find?.((clip) => clip.clipId === selectedId);
    if (!!clip && !!selectedId) {
      bpShowcaseLoadVideo(
        user?.uri || '',
        selectedId,
        clip?.privacy?.view,
        clip?.user?.uri,
        viewer,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips, selectedId, user?.uri]);

  return (
    <LoginModalProvider>
      <ShowcaseNavigationModule isShowcaseEmpty={isShowcaseEmpty} />
      <ClipLayout
        canSeePlaylist
        showcaseConfig={{ isShowcaseClipView: isClipView, isShowcaseEmpty }}
        isLiveEventView={isLiveEvent && isSideModuleOpen}
        showComments={isVideoClip && showCommentsView}
      >
        <ClipLayout.Media ref={mediaAreaRef}>
          <AnimatePresence>
            {isClipView ? (
              <IframePlayerModule bannerHeight={bannerHeight} />
            ) : (
              <BannerModule
                bannerHeight={bannerHeight}
                isShowcaseEmpty={isShowcaseEmpty}
                showVideoDetails={showVideoDetails}
              />
            )}
          </AnimatePresence>
        </ClipLayout.Media>

        {isClipView ? (
          <ClipLayout.ContentStack>
            <VideoDetailsModule
              enableLike={enableLike}
              showDateAdded={showDateAdded}
              showDescription={showDescription}
              showProfileImage={showProfileImage}
              showProfileName={showProfileName}
              showTitle={showTitle}
              showViews={showViews}
            >
              {isLiveEvent ? (
                <EventActionBarModule isInteractionToolsEnabled={isInteractionToosEnabled} />
              ) : null}
              {isVideoClip && (
                <VideoActionBarModule
                  clipId={clipId}
                  canInteract={canInteract}
                  clipRequestId={clipRequestId}
                  showComments={showCommentsView && !isDesktop}
                  showShare={showShareView}
                  showLike={showLikeView}
                  showAskAi={canSeeVimeoAi}
                  canLike={canLike}
                  showDownload={showDownload}
                  isVideoProcessing={!!isVideoProcessing}
                />
              )}
            </VideoDetailsModule>
          </ClipLayout.ContentStack>
        ) : null}

        <ClipLayout.ShowcasePlaylist>
          <Box position="relative" w={commonWrapperWidth} left={commonWrapperLeft}>
            {isClipView ? (
              <ClipPlaylistModule
                playerContainerRef={mediaAreaRef}
                bannerHeight={bannerHeight}
                isNewCustomizeControls={isNewCustomizeControls}
              />
            ) : (
              <GalleryPlaylistModule
                playerContainerRef={mediaAreaRef}
                bannerHeight={bannerHeight}
                isNewCustomizeControls={isNewCustomizeControls}
              />
            )}
          </Box>
        </ClipLayout.ShowcasePlaylist>

        <Show above="md">
          {isVideoClip && showCommentsView ? (
            <ClipLayout.Comments globalNavigationHidden={hideNav}>
              <CommentsModule
                clipId={clipId}
                clipOwnerUri={clip?.user?.uri}
                clipRequestId={clipRequestId}
                privacyView={clip?.privacy?.view}
                showComments={showCommentsView}
              />
            </ClipLayout.Comments>
          ) : null}

          {isLiveEvent ? (
            <ClipLayout.SideModule globalNavigationHidden={hideNav}>
              <LiveEngagementToolModule
                roomId={roomId}
                isEventLoading={isClipLoading}
                playerContainerRef={mediaAreaRef}
                setIsInteractionToolsEnabled={setIsInteractionToolsEnabled}
              />
            </ClipLayout.SideModule>
          ) : null}

          {isSideModuleOpen ? (
            <ClipLayout.SideModule globalNavigationHidden={hideNav}>
              {activeSideModule === 'TRANSCRIPT' ? <Center h="100%">SIDE MODULE</Center> : null}
              {activeSideModule === 'CHAPTERS' ? <Center h="100%">SIDE MODULE</Center> : null}
              {activeSideModule === 'VIMEO_AI' && canSeeVimeoAi ? (
                <VimeoAiModule key={clipId} clipId={clipId} clipRequestId={clipRequestId} />
              ) : null}
            </ClipLayout.SideModule>
          ) : null}
        </Show>

        <Hide above="md">
          {/* Mobile Drawer will unmount, but live engagement tool currently required to not to unmount */}
          {isVideoClip ? (
            <ClipLayout.MobileDrawer playerContainerRef={mediaAreaRef}>
              {activeMobileDrawer === 'TRANSCRIPT' ? <Center h="100%">SIDE MODULE</Center> : null}
              {activeMobileDrawer === 'CHAPTERS' ? <Center h="100%">SIDE MODULE</Center> : null}
              {activeMobileDrawer === 'VIMEO_AI' && canSeeVimeoAi ? (
                <VimeoAiModule key={clipId} clipId={clipId} clipRequestId={clipRequestId} />
              ) : null}
              {activeMobileDrawer === 'COMMENTS' ? (
                <CommentsModule
                  clipId={clipId}
                  clipOwnerUri={clip?.user?.uri}
                  clipRequestId={clipRequestId}
                  privacyView={clip?.privacy?.view}
                  showComments={showCommentsView}
                />
              ) : null}
            </ClipLayout.MobileDrawer>
          ) : (
            <>
              <LiveEngagementToolModule
                isEventLoading={isClipLoading}
                isMobileDrawer
                playerContainerRef={mediaAreaRef}
                setIsInteractionToolsEnabled={setIsInteractionToolsEnabled}
                roomId={roomId}
              />
            </>
          )}
        </Hide>
      </ClipLayout>
    </LoginModalProvider>
  );
};
